// src/pages/GroupsPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  Tabs,
  Tab,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { THEME_COLORS } from '../styles/theme';
import { buildInviteContent, buildWhatsAppLink } from '../features/collaboration/services/inviteLinkService';
import { sendInviteEmail } from '../features/collaboration/services/inviteEmailService';
import {
  createGroup,
  fetchPublicGroups,
  fetchUserGroups,
  fetchGroupInvitesForUser,
  sendGroupInvite,
  respondToGroupInvite,
  requestToJoinGroup,
  joinGroupViaLink,
  fetchGroupJoinRequests,
  respondToJoinRequest,
  fetchConnections
} from '../features/collaboration/services/collaborationService';

const DEFAULT_FILTERS = {
  subjects: [],
  grades: [],
  schools: [],
  districts: [],
  projects: []
};

const GroupsPage = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const joinCode = searchParams.get('code');
  const joinAttemptedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState(0);

  const [myGroups, setMyGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [invites, setInvites] = useState([]);
  const [connections, setConnections] = useState([]);
  const [joinRequestsByGroup, setJoinRequestsByGroup] = useState({});

  const [createForm, setCreateForm] = useState({
    name: '',
    purpose: '',
    visibility: 'private',
    joinPolicy: 'invite',
    allowLinkJoin: false,
    filters: DEFAULT_FILTERS
  });

  const [discoverFilters, setDiscoverFilters] = useState({
    text: '',
    subjects: [],
    grades: [],
    schools: [],
    districts: [],
    projects: []
  });

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteGroup, setInviteGroup] = useState(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteWhatsApp, setInviteWhatsApp] = useState('');

  const myGroupIds = useMemo(() => new Set(myGroups.map((group) => group.id)), [myGroups]);
  const managedGroups = useMemo(
    () => myGroups.filter((group) => ['owner', 'admin'].includes(group.membership?.role)),
    [myGroups]
  );

  const filteredPublicGroups = useMemo(() => {
    const normalized = (value) => String(value || '').trim().toLowerCase();
    const matchCriteria = (groupValues, criteria) => {
      if (!criteria || criteria.length === 0) return true;
      const normalizedGroupValues = (groupValues || []).map(normalized);
      return criteria.some((value) => normalizedGroupValues.includes(normalized(value)));
    };

    return publicGroups.filter((group) => {
      if (myGroupIds.has(group.id)) return false;
      const textMatch = normalized(group.name).includes(normalized(discoverFilters.text));
      if (!textMatch) return false;
      const filters = group.filters || {};
      if (!matchCriteria(filters.subjects, discoverFilters.subjects)) return false;
      if (!matchCriteria(filters.grades, discoverFilters.grades)) return false;
      if (!matchCriteria(filters.schools, discoverFilters.schools)) return false;
      if (!matchCriteria(filters.districts, discoverFilters.districts)) return false;
      if (!matchCriteria(filters.projects, discoverFilters.projects)) return false;
      return true;
    });
  }, [publicGroups, discoverFilters, myGroupIds]);

  const inviterName = userProfile?.name || currentUser?.displayName || 'Teachmate';
  const groupLink = inviteGroup
    ? `${window.location.origin}/groups/${inviteGroup.id}${inviteGroup.joinCode ? `?code=${inviteGroup.joinCode}` : ''}`
    : '';
  const inviteBody = buildInviteContent({
    type: 'group',
    inviterName,
    groupName: inviteGroup?.name,
    link: groupLink,
    note: inviteMessage
  });
  const inviteSubject = `${inviterName} invited you to join ${inviteGroup?.name || 'a Teachmate group'}`;
  const emailValid = !inviteEmail || /\S+@\S+\.\S+/.test(inviteEmail);
  const whatsappValid = !inviteWhatsApp || /[0-9]/.test(inviteWhatsApp);


  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const results = await Promise.allSettled([
          fetchUserGroups(currentUser.uid),
          fetchPublicGroups(50),
          fetchGroupInvitesForUser(currentUser.uid),
          fetchConnections(currentUser.uid)
        ]);

        const [groupsResult, publicResult, invitesResult, connectionsResult] = results;

        if (groupsResult.status === 'fulfilled') {
          setMyGroups(groupsResult.value || []);
        } else {
          console.error('Error loading user groups:', groupsResult.reason);
          setMyGroups([]);
        }

        if (publicResult.status === 'fulfilled') {
          setPublicGroups(publicResult.value || []);
        } else {
          console.error('Error loading public groups:', publicResult.reason);
          setPublicGroups([]);
        }

        if (invitesResult.status === 'fulfilled') {
          setInvites(invitesResult.value || []);
        } else {
          console.error('Error loading group invites:', invitesResult.reason);
          setInvites([]);
        }

        if (connectionsResult.status === 'fulfilled') {
          setConnections((connectionsResult.value || []).filter((conn) => conn.status === 'active'));
        } else {
          console.error('Error loading connections:', connectionsResult.reason);
          setConnections([]);
        }

        const failedCount = results.filter((result) => result.status === 'rejected').length;
        if (failedCount > 0) {
          setNotice({
            type: 'warning',
            message: 'Some data could not be loaded. Try refreshing if needed.'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  useEffect(() => {
    const loadRequests = async () => {
      if (!managedGroups.length) {
        setJoinRequestsByGroup({});
        return;
      }
      const requestsMap = {};
      for (const group of managedGroups) {
        try {
          const requests = await fetchGroupJoinRequests(group.id);
          requestsMap[group.id] = requests;
        } catch (error) {
          console.error('Error loading join requests:', error);
        }
      }
      setJoinRequestsByGroup(requestsMap);
    };
    loadRequests();
  }, [managedGroups]);

  useEffect(() => {
    const attemptJoin = async () => {
      if (!groupId || !currentUser || joinAttemptedRef.current) return;
      joinAttemptedRef.current = true;
      try {
        const result = await joinGroupViaLink(groupId, currentUser.uid, joinCode || '');
        if (result?.status === 'joined') {
          setNotice({ type: 'success', message: 'You joined the group.' });
        }
      } catch (error) {
        setNotice({ type: 'error', message: error.message || 'Unable to join via link.' });
      }
    };

    attemptJoin();
  }, [groupId, currentUser, joinCode]);

  const handleCreateGroup = async () => {
    if (!createForm.name.trim()) {
      setNotice({ type: 'error', message: 'Group name is required.' });
      return;
    }

    try {
      const group = await createGroup(currentUser.uid, createForm);
      setMyGroups((prev) => [group, ...prev]);
      setCreateForm({
        name: '',
        purpose: '',
        visibility: 'private',
        joinPolicy: 'invite',
        allowLinkJoin: false,
        filters: DEFAULT_FILTERS
      });
      setNotice({ type: 'success', message: 'Group created.' });
    } catch (error) {
      console.error('Create group error:', error);
      setNotice({ type: 'error', message: error.message || 'Failed to create group.' });
    }
  };

  const handleInvite = async (userId) => {
    if (!inviteGroup) return;
    try {
      await sendGroupInvite(inviteGroup.id, currentUser.uid, userId, inviteMessage);
      setNotice({ type: 'success', message: 'Invite sent.' });
      setInviteDialogOpen(false);
      setInviteMessage('');
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Failed to send invite.' });
    }
  };

  const handleRespondInvite = async (invite, accept) => {
    try {
      await respondToGroupInvite(invite.groupId, invite.id, currentUser.uid, accept);
      setInvites((prev) => prev.filter((item) => item.id !== invite.id));
      if (accept) {
        const updatedGroups = await fetchUserGroups(currentUser.uid);
        setMyGroups(updatedGroups || []);
      }
      setNotice({ type: 'success', message: accept ? 'Invite accepted.' : 'Invite declined.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Failed to respond to invite.' });
    }
  };

  const handleJoinGroup = async (group) => {
    try {
      const result = await joinGroupViaLink(group.id, currentUser.uid, group.joinCode || '');
      if (result?.status === 'already_member') {
        setNotice({ type: 'info', message: 'You are already a member.' });
      } else {
        setNotice({ type: 'success', message: 'You joined the group.' });
        const updatedGroups = await fetchUserGroups(currentUser.uid);
        setMyGroups(updatedGroups || []);
      }
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Failed to join group.' });
    }
  };

  const handleRequestJoin = async (group) => {
    try {
      const result = await requestToJoinGroup(group.id, currentUser.uid);
      if (result?.status === 'pending') {
        setNotice({ type: 'info', message: 'Join request already pending.' });
      } else {
        setNotice({ type: 'success', message: 'Join request submitted.' });
      }
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Failed to request to join.' });
    }
  };

  const handleRespondRequest = async (groupIdToUse, userId, approve) => {
    try {
      await respondToJoinRequest(groupIdToUse, userId, approve, currentUser.uid);
      const refreshed = await fetchGroupJoinRequests(groupIdToUse);
      setJoinRequestsByGroup((prev) => ({ ...prev, [groupIdToUse]: refreshed }));
      if (approve) {
        const updatedGroups = await fetchUserGroups(currentUser.uid);
        setMyGroups(updatedGroups || []);
      }
      setNotice({ type: 'success', message: approve ? 'Request approved.' : 'Request declined.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Failed to respond to request.' });
    }
  };

  const copyLink = async (group) => {
    const link = `${window.location.origin}/groups/${group.id}?code=${group.joinCode || ''}`;
    try {
      await navigator.clipboard.writeText(link);
      setNotice({ type: 'success', message: 'Group link copied.' });
    } catch (error) {
      setNotice({ type: 'error', message: 'Failed to copy link.' });
    }
  };

  const renderFilters = (filters) => {
    const filterItems = [];
    if (filters?.subjects?.length) filterItems.push(`Subjects: ${filters.subjects.join(', ')}`);
    if (filters?.grades?.length) filterItems.push(`Grades: ${filters.grades.join(', ')}`);
    if (filters?.schools?.length) filterItems.push(`Schools: ${filters.schools.join(', ')}`);
    if (filters?.districts?.length) filterItems.push(`Districts: ${filters.districts.join(', ')}`);
    if (filters?.projects?.length) filterItems.push(`Projects: ${filters.projects.join(', ')}`);

    return (
      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
        {filterItems.map((item) => (
          <Chip key={item} label={item} size="small" />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" fontWeight="bold" color={THEME_COLORS.primary} mb={2}>
        Groups
      </Typography>

      {notice && (
        <Alert severity={notice.type} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1.6fr' }} gap={3}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={1}>
              Create Group
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Set a clear purpose and limit membership by subject, grade, school, district, or project.
            </Typography>
            <TextField
              label="Group name"
              fullWidth
              value={createForm.name}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Purpose"
              fullWidth
              multiline
              minRows={2}
              value={createForm.purpose}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, purpose: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="visibility-label">Visibility</InputLabel>
              <Select
                labelId="visibility-label"
                label="Visibility"
                value={createForm.visibility}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    visibility: e.target.value
                  }))
                }
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="join-policy-label">Join policy</InputLabel>
              <Select
                labelId="join-policy-label"
                label="Join policy"
                value={createForm.joinPolicy}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    joinPolicy: e.target.value,
                    allowLinkJoin: e.target.value === 'open'
                  }))
                }
              >
                <MenuItem value="invite">Invite only</MenuItem>
                <MenuItem value="request">Request to join</MenuItem>
                <MenuItem value="open">Anyone with link</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={createForm.filters.subjects}
              onChange={(e, value) =>
                setCreateForm((prev) => ({
                  ...prev,
                  filters: { ...prev.filters, subjects: value }
                }))
              }
              renderInput={(params) => <TextField {...params} label="Subjects" />}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={createForm.filters.grades}
              onChange={(e, value) =>
                setCreateForm((prev) => ({
                  ...prev,
                  filters: { ...prev.filters, grades: value }
                }))
              }
              renderInput={(params) => <TextField {...params} label="Grades" />}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={createForm.filters.schools}
              onChange={(e, value) =>
                setCreateForm((prev) => ({
                  ...prev,
                  filters: { ...prev.filters, schools: value }
                }))
              }
              renderInput={(params) => <TextField {...params} label="Schools" />}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={createForm.filters.districts}
              onChange={(e, value) =>
                setCreateForm((prev) => ({
                  ...prev,
                  filters: { ...prev.filters, districts: value }
                }))
              }
              renderInput={(params) => <TextField {...params} label="Districts" />}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={createForm.filters.projects}
              onChange={(e, value) =>
                setCreateForm((prev) => ({
                  ...prev,
                  filters: { ...prev.filters, projects: value }
                }))
              }
              renderInput={(params) => <TextField {...params} label="Projects" />}
              sx={{ mb: 2 }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleCreateGroup}
              disabled={!currentUser || loading}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Create Group
            </Button>
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ mb: 2 }}>
              <Tab label="My Groups" />
              <Tab label="Discover" />
              <Tab label="Invites" />
              <Tab label="Requests" />
            </Tabs>

            {tab === 0 && (
              <Box>
                {myGroups.length === 0 ? (
                  <Typography color="textSecondary">No groups yet.</Typography>
                ) : (
                  myGroups.map((group) => (
                    <Box key={group.id} mb={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {group.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {group.purpose || 'No purpose provided.'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {group.visibility} • {group.joinPolicy}
                      </Typography>
                      {renderFilters(group.filters)}
                      <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
                        <Button size="small" onClick={() => navigate(`/groups/${group.id}`)}>
                          View Group
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setInviteGroup(group);
                            setInviteDialogOpen(true);
                          }}
                        >
                          Invite Contacts
                        </Button>
                        {group.allowLinkJoin && (
                          <Button size="small" onClick={() => copyLink(group)}>
                            Copy Link
                          </Button>
                        )}
                      </Box>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  ))
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box>
                <TextField
                  label="Search groups"
                  fullWidth
                  value={discoverFilters.text}
                  onChange={(e) => setDiscoverFilters((prev) => ({ ...prev, text: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={discoverFilters.subjects}
                  onChange={(e, value) =>
                    setDiscoverFilters((prev) => ({ ...prev, subjects: value }))
                  }
                  renderInput={(params) => <TextField {...params} label="Filter by subjects" />}
                  sx={{ mb: 2 }}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={discoverFilters.grades}
                  onChange={(e, value) =>
                    setDiscoverFilters((prev) => ({ ...prev, grades: value }))
                  }
                  renderInput={(params) => <TextField {...params} label="Filter by grades" />}
                  sx={{ mb: 2 }}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={discoverFilters.schools}
                  onChange={(e, value) =>
                    setDiscoverFilters((prev) => ({ ...prev, schools: value }))
                  }
                  renderInput={(params) => <TextField {...params} label="Filter by schools" />}
                  sx={{ mb: 2 }}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={discoverFilters.districts}
                  onChange={(e, value) =>
                    setDiscoverFilters((prev) => ({ ...prev, districts: value }))
                  }
                  renderInput={(params) => <TextField {...params} label="Filter by districts" />}
                  sx={{ mb: 2 }}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={discoverFilters.projects}
                  onChange={(e, value) =>
                    setDiscoverFilters((prev) => ({ ...prev, projects: value }))
                  }
                  renderInput={(params) => <TextField {...params} label="Filter by projects" />}
                  sx={{ mb: 2 }}
                />

                {filteredPublicGroups.length === 0 ? (
                  <Typography color="textSecondary">No public groups match your filters.</Typography>
                ) : (
                  filteredPublicGroups.map((group) => (
                    <Box key={group.id} mb={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {group.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {group.purpose || 'No purpose provided.'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {group.visibility} • {group.joinPolicy}
                      </Typography>
                      {renderFilters(group.filters)}
                      <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
                        <Button size="small" onClick={() => navigate(`/groups/${group.id}`)}>
                          View Group
                        </Button>
                        {group.joinPolicy === 'open' && group.allowLinkJoin && (
                          <Button size="small" variant="contained" onClick={() => handleJoinGroup(group)}>
                            Join
                          </Button>
                        )}
                        {group.joinPolicy === 'request' && (
                          <Button size="small" variant="outlined" onClick={() => handleRequestJoin(group)}>
                            Request to Join
                          </Button>
                        )}
                        {group.joinPolicy === 'invite' && (
                          <Button size="small" disabled>
                            Invite Only
                          </Button>
                        )}
                        {group.allowLinkJoin && (
                          <Button size="small" onClick={() => copyLink(group)}>
                            Copy Link
                          </Button>
                        )}
                      </Box>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  ))
                )}
              </Box>
            )}

            {tab === 2 && (
              <Box>
                {invites.length === 0 ? (
                  <Typography color="textSecondary">No group invites.</Typography>
                ) : (
                  invites.map((invite) => (
                    <Box key={invite.id} mb={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {invite.group?.name || invite.groupName || 'Group Invite'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {invite.group?.purpose || invite.groupPurpose || 'No purpose provided.'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Invited by {invite.fromUserId}
                      </Typography>
                      {invite.message && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Message: {invite.message}
                        </Typography>
                      )}
                      <Box display="flex" gap={1} mt={1.5}>
                        <Button size="small" variant="contained" onClick={() => handleRespondInvite(invite, true)}>
                          Accept
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => handleRespondInvite(invite, false)}>
                          Decline
                        </Button>
                      </Box>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  ))
                )}
              </Box>
            )}

            {tab === 3 && (
              <Box>
                {managedGroups.length === 0 ? (
                  <Typography color="textSecondary">No groups to manage.</Typography>
                ) : (
                  managedGroups.map((group) => (
                    <Box key={group.id} mb={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {group.name}
                      </Typography>
                      {(joinRequestsByGroup[group.id] || []).length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          No pending requests.
                        </Typography>
                      ) : (
                        (joinRequestsByGroup[group.id] || []).map((request) => (
                          <Box key={request.id} mt={1} mb={1} display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {request.user?.name || 'User'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {request.user?.title || ''}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1}>
                              <Button size="small" variant="contained" onClick={() => handleRespondRequest(group.id, request.userId, true)}>
                                Approve
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => handleRespondRequest(group.id, request.userId, false)}>
                                Decline
                              </Button>
                            </Box>
                          </Box>
                        ))
                      )}
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  ))
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={inviteDialogOpen}
        onClose={() => {
          setInviteDialogOpen(false);
          setInviteMessage('');
          setInviteEmail('');
          setInviteWhatsApp('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Invite Contacts</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Invite by Email or WhatsApp
            </Typography>
            <TextField
              label="Email address (optional)"
              fullWidth
              size="small"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              error={!emailValid}
              helperText={!emailValid ? 'Enter a valid email address.' : ' '}
              sx={{ mb: 1 }}
            />
            <TextField
              label="WhatsApp number (optional)"
              fullWidth
              size="small"
              value={inviteWhatsApp}
              onChange={(e) => setInviteWhatsApp(e.target.value)}
              error={!whatsappValid}
              helperText={!whatsappValid ? 'Enter a valid phone number.' : ' '}
              sx={{ mb: 1 }}
            />
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                disabled={!inviteEmail.trim() || !emailValid}
                onClick={async () => {
                  if (!inviteEmail.trim() || !emailValid) return;
                  try {
                    await sendInviteEmail({
                      toEmail: inviteEmail.trim(),
                      subject: inviteSubject,
                      body: inviteBody,
                      type: 'group',
                      link: groupLink,
                      groupId: inviteGroup?.id || null,
                      groupName: inviteGroup?.name || null
                    });
                    setNotice({ type: 'success', message: 'Email invite sent.' });
                  } catch (error) {
                    console.error('Error sending invite email:', error);
                    setNotice({ type: 'error', message: error.message || 'Failed to send email invite.' });
                  }
                }}
              >
                Send Email Invite
              </Button>
              <Button
                variant="outlined"
                disabled={!inviteWhatsApp.trim() || !whatsappValid}
                onClick={() => {
                  const url = buildWhatsAppLink(inviteWhatsApp, inviteBody);
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                Send WhatsApp Invite
              </Button>
            </Box>
          </Box>
          <TextField
            label="Invite message (optional)"
            fullWidth
            multiline
            minRows={2}
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            sx={{ mb: 2 }}
          />
          {connections.length === 0 ? (
            <Typography color="textSecondary">No active connections.</Typography>
          ) : (
            <List>
              {connections.map((connection) => (
                <ListItem key={connection.id} secondaryAction={
                  <Button size="small" onClick={() => handleInvite(connection.user?.id)}>
                    Invite
                  </Button>
                }>
                  <ListItemAvatar>
                    <Avatar src={connection.user?.profileImage || undefined}>
                      {(connection.user?.name || 'U').charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={connection.user?.name || 'User'}
                    secondary={connection.user?.title || ''}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setInviteDialogOpen(false);
              setInviteMessage('');
              setInviteEmail('');
              setInviteWhatsApp('');
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupsPage;
